import { motion } from "framer-motion";
import { Referral, ReferralStep, UserGender } from "@/generated-api";
import { CheckIcon, XIcon } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import moneybag from "@/assets/money_bag.png";

export default function ReferralStepTracker({
  referral,
}: {
  referral: Referral;
}) {
  const t = useTranslation();
  const steps = [
    { key: ReferralStep.APPLYING, label: t.REFERRAL_STEPS.APPLYING },
    {
      key: ReferralStep.APPLIED,
      label: t.REFERRAL_STEPS.APPLIED,
    },
  ];

  if (referral.step === ReferralStep.DENIED) {
    steps.push({
      key: ReferralStep.DENIED,
      label: t.REFERRAL_STEPS.DENIED,
    });
  } else {
    steps.push({
      key: ReferralStep.APPROVED,
      label: t.REFERRAL_STEPS.APPROVED,
    });
  }

  if (referral.gender === UserGender.MALE) {
    steps.push({
      key: ReferralStep.SUBSCRIBED,
      label: t.REFERRAL_STEPS.SUBSCRIBED,
    });
  }

  const currentIndex = steps.findIndex((s) => s.key === referral.step);
  const finalStepCheckmark =
    (referral.gender === UserGender.FEMALE &&
      referral.step === ReferralStep.APPROVED) ||
    (referral.gender === UserGender.MALE &&
      referral.step === ReferralStep.SUBSCRIBED);

  return (
    <div className="w-full max-w-md mx-auto px-4 mb-7">
      <div className="relative">
        <div className="absolute top-2 left-0 right-0 h-2 bg-zinc-800 rounded-full" />

        <motion.div
          className={`absolute top-2 left-0 h-2 ${
            referral.step === ReferralStep.DENIED ? "bg-red-900" : "bg-zinc-200"
          } rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }}
          transition={{ duration: 0.4 }}
        />

        {/* Step Dots */}
        <div className="relative z-10 flex justify-between items-center">
          {steps.map((s, i) => {
            const isActive = i <= currentIndex;
            return (
              <div key={s.key} className="relative flex flex-col items-center">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    isActive
                      ? referral.step === ReferralStep.DENIED
                        ? "bg-red-900 text-black"
                        : "bg-zinc-200 text-black"
                      : "bg-zinc-700"
                  }`}
                >
                  {i === steps.length - 1 && referral.reward_paid_at ? (
                    <img src={moneybag} className="w-4 h-4" />
                  ) : i === steps.length - 1 && finalStepCheckmark ? (
                    <CheckIcon className="h-4 w-4" strokeWidth={4} />
                  ) : s.key === ReferralStep.DENIED ? (
                    <XIcon className="h-4 w-4" strokeWidth={4} />
                  ) : null}
                </div>
                <span className="flex flex-col absolute top-8 w-max text-sm text-center text-zinc-500 whitespace-nowrap">
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
